import type { SentryInstance } from 'shared';
import type { Router } from 'vue-router';

import {
  clearRecoveryAttempts,
  executeChunkRecovery,
  planChunkRecovery,
} from './recover-from-chunk-load-error';

/**
 * Route pages are loaded via dynamic `import()` (route-level code splitting),
 * so their JS/CSS chunks carry content-hashed filenames. A chunk load can fail
 * on a flaky mobile-webview connection that drops a request mid-flight, or when
 * a client still running an older build requests a chunk that a newer deploy
 * has already replaced.
 *
 * We log the failure to Sentry — enriched with the page that failed, the running
 * build, and the network / visibility state at the moment of failure — and then
 * attempt recovery, because a failed route navigation otherwise leaves the user
 * on a dead screen. See `recover-from-chunk-load-error.ts` for why the CSS and
 * JS cases need different strategies; the short version is that a plain retry
 * cannot work for a JS chunk, since the browser will not re-request a module URL
 * it has already recorded as failed.
 *
 * Lives here rather than in an app because every mini app with route-level code
 * splitting hits the same failure. Sentry cannot be imported from a shared
 * package though — each app owns its own instance — so it is injected, following
 * `withFirebase(sentryModel)`.
 */

/**
 * Each entry pairs a browser / bundler error dialect with a short label, so the
 * log records which engine produced the failure — handy for spotting a
 * device- or browser-specific cluster.
 */
const CHUNK_LOAD_ERROR_PATTERNS: ReadonlyArray<{
  engine: string;
  pattern: RegExp;
}> = [
  // Chromium
  {
    engine: 'chromium',
    pattern: /Failed to fetch dynamically imported module/i,
  },
  // Firefox
  { engine: 'firefox', pattern: /error loading dynamically imported module/i },
  // Safari / WebKit
  { engine: 'webkit', pattern: /Importing a module script failed/i },
  // CSS code-split chunk (Vite's preload helper throws this verbatim)
  { engine: 'css-preload', pattern: /Unable to preload CSS/i },
  // Legacy webpack-style messages, kept defensively
  { engine: 'legacy', pattern: /Loading( CSS)? chunk .* failed/i },
];

/**
 * Classify an error as a chunk-load failure and, if so, name the dialect that
 * matched. Returns `engine: ''` for non-chunk errors.
 */
const matchChunkLoadError = (
  error: unknown,
): { isChunkLoadError: boolean; engine: string } => {
  if (!(error instanceof Error)) {
    return { isChunkLoadError: false, engine: '' };
  }

  if (error.name === 'ChunkLoadError') {
    return { isChunkLoadError: true, engine: 'chunk-load-error-name' };
  }

  const matched = CHUNK_LOAD_ERROR_PATTERNS.find(({ pattern }) =>
    pattern.test(error.message),
  );

  return matched
    ? { isChunkLoadError: true, engine: matched.engine }
    : { isChunkLoadError: false, engine: '' };
};

/**
 * The failed chunk's URL — present in Chromium/Firefox messages
 * ("…imported module: <url>") and CSS-preload messages; absent for Safari's
 * generic "Importing a module script failed.".
 */
const extractFailedChunk = (error: unknown): string => {
  if (!(error instanceof Error)) {
    return 'unknown';
  }

  const match = error.message.match(/https?:\/\/\S+?\.(?:m?js|css)\b/i);
  return match ? match[0] : 'unknown';
};

const routeName = (name: unknown): string =>
  typeof name === 'string' && name ? name : '(unnamed)';

const readEnvString = (value: unknown): string =>
  typeof value === 'string' && value ? value : 'unknown';

/**
 * Non-standard `navigator.connection`, but widely available in mobile webviews.
 * Lets us see whether failures cluster on slow / limited connections (→
 * transient network) rather than being spread evenly (→ deploy / CDN).
 */
type NetworkInformation = {
  effectiveType?: string;
  downlink?: number;
  rtt?: number;
  saveData?: boolean;
};

const getNetworkInformation = (): NetworkInformation => {
  if (typeof navigator === 'undefined') {
    return {};
  }

  return (
    (navigator as Navigator & { connection?: NetworkInformation }).connection ??
    {}
  );
};

/**
 * Passive snapshot of the environment at failure time. Every field is read
 * synchronously from globals — no extra network request is issued.
 */
const getDiagnosticContext = (): Record<string, unknown> => {
  const connection = getNetworkInformation();

  return {
    // Which build the failing client is running. If failures pile up on builds
    // older than the currently-deployed one, that points at a stale deploy
    // (on staging, old chunks are pruned on every deploy).
    appVersion: readEnvString(import.meta.env.VITE_APP_VERSION),
    runtimeEnv: readEnvString(import.meta.env.VITE_RUNTIME_ENV),
    online: typeof navigator !== 'undefined' ? navigator.onLine : 'unknown',
    // Webviews throttle / kill network while backgrounded; a 'hidden' state at
    // failure time is a strong hint the OS suspended the request.
    visibility:
      typeof document !== 'undefined' ? document.visibilityState : 'unknown',
    connectionType: connection.effectiveType ?? 'unknown',
    downlink: connection.downlink ?? 'unknown',
    rtt: connection.rtt ?? 'unknown',
    saveData: connection.saveData ?? 'unknown',
  };
};

/**
 * Chunk-load failures seen this page-load session. A burst within one session
 * reads differently (client stuck on a dead build) than isolated one-offs
 * across many sessions (transient drops).
 */
let sessionChunkErrorCount = 0;

export type HandleChunkLoadErrorOptions = {
  router: Router;
  /**
   * The consuming app's Sentry instance. Injected because a shared package must
   * not reach into an app's `@/shared/model` — same contract as `withFirebase`.
   */
  sentryModel: SentryInstance;
  /**
   * Sentry error prefix for this app, e.g. `sentryModel.errorPrefix.AVIA`. Keeps
   * chunk failures grouped under the app that produced them.
   */
  errorPrefix: string;
  /**
   * Storage-key prefix scoping the retry budget to this app (`free-avia`,
   * `free-rw`, `free-cabinet`). See `recover-from-chunk-load-error.ts` for why
   * one shared ledger across mini apps would be wrong.
   */
  storagePrefix: string;
};

/**
 * Report a failure with the extras nested on the scope.
 *
 * `captureException`'s second argument is a `CaptureContext`, which only reads
 * known keys — passing the extras bag flat matched none of them, so every field
 * was silently dropped and reports arrived with no diagnostic context at all.
 * Setting them on the scope avoids that trap entirely, and keeps to the one-arg
 * `captureException` both `SentryInstance` variants expose.
 */
const reportFailure = ({
  sentryModel,
  errorPrefix,
  code,
  error,
  extra,
}: {
  sentryModel: SentryInstance;
  errorPrefix: string;
  code: string;
  error: Error;
  extra: Record<string, unknown>;
}): void => {
  sentryModel.withScope(scope => {
    scope.setLevel(sentryModel.level.ERROR);
    scope.setExtras(extra);

    sentryModel.captureException(
      sentryModel.getReadableError(
        sentryModel.createCustomErrorText(errorPrefix, code),
        error,
      ),
    );
  });
};

/**
 * Log chunk-load failures that surface during navigation to Sentry, tagged with
 * the page being navigated to (and the matched route chain), the page navigated
 * from, the failed chunk URL, the running build, and the network / visibility
 * state — so the report pinpoints which page's chunk failed, from where, on
 * which build, and under what conditions. Then recover, per the strategies in
 * `recover-from-chunk-load-error.ts`.
 */
export const handleChunkLoadError = ({
  router,
  sentryModel,
  errorPrefix,
  storagePrefix,
}: HandleChunkLoadErrorOptions): void => {
  /**
   * A route that navigates successfully is not in a reload loop, so release its
   * one-shot recovery budget. Otherwise the first transient failure would spend
   * the budget for the rest of the (long-lived) webview session and leave the
   * route unrecoverable on a later, unrelated failure.
   */
  router.afterEach(to => {
    clearRecoveryAttempts({ path: to.fullPath, storagePrefix });
  });

  router.onError((error, to, from) => {
    const { isChunkLoadError, engine } = matchChunkLoadError(error);
    if (!isChunkLoadError) {
      return;
    }

    const err = error as Error;
    sessionChunkErrorCount += 1;

    /**
     * Planned before reporting and executed after, so the report names the
     * strategy even when that strategy is a hard navigation — which starts
     * unloading the page and would otherwise cancel the request in flight.
     */
    const recoveryPlan = planChunkRecovery({ to, error, storagePrefix });

    reportFailure({
      sentryModel,
      errorPrefix,
      code: 'CHUNK_LOAD',
      error: err,
      extra: {
        toRoute: routeName(to.name),
        toPath: to.fullPath,
        toMatched: to.matched.map(record => routeName(record.name)).join(' > '),
        fromRoute: routeName(from.name),
        fromPath: from.fullPath,
        failedChunk: extractFailedChunk(error),
        engine,
        errorName: err.name,
        errorMessage: err.message,
        sessionChunkErrorCount,
        recoveryPlan: recoveryPlan.kind,
        ...getDiagnosticContext(),
      },
    });

    /**
     * `router.onError` is synchronous, so this is intentionally not awaited.
     * A failure inside recovery must not replace the original chunk error.
     */
    executeChunkRecovery({
      router,
      to,
      plan: recoveryPlan,
      storagePrefix,
    }).catch(recoveryError => {
      reportFailure({
        sentryModel,
        errorPrefix,
        code: 'CHUNK_RECOVERY',
        error: recoveryError as Error,
        extra: { toPath: to.fullPath, recoveryPlan: recoveryPlan.kind },
      });
    });
  });
};
